// liveStream.js — the real per-host realtime transport.
//
// A self-contained fetch-based SSE lifecycle wrapped behind a tiny seam so
// apiClient can wire it without an import cycle: this module imports NOTHING
// from apiClient (it only takes callbacks).
//
// Protocol (kgsm-api StreamProtocol / kgsm-docs reference/realtime-stream.md):
//   server → client : data: { topic, type, data }\n\n
//   no initial snapshot — the client REST-hydrates on (re)connect and the stream
//   carries deltas (kgsm-docs reference/realtime-stream.md). So on every (re)open we call onOpen so the owner can
//   re-hydrate the stores.
//
// One SSE stream to one host's `/api/v1/stream`. Topics are fixed at connect
// via ?topics=. Changing topics = open another stream / close this one.

import { readSseStream } from "@thekrystalship/krystal/lib/sse";

const RECONNECT_BASE = 2500, RECONNECT_CAP = 12000;
// How long a stream must have lived for its clean end to be an expiry rather than a refusal.
const STABLE_MS = 1000;
const backoff = (n) => Math.min(RECONNECT_BASE * Math.pow(2, n), RECONNECT_CAP);

// createSseStream({ url, request, onOpen, onMessage, onMode })
//   url           — the full SSE URL with ?topics=... already baked in
//   request       — (url, init) => Promise<Response>, called at each connect. It carries the session's
//                   bearer and renews a refused dial once; it rejects when there is no session.
//   onOpen        — () => void, fired after the connected comment arrives (mode→live)
//   onMessage     — (msg:{topic,type,data}) => void, one parsed server frame
//   onMode        — (mode:"live"|"connecting"|"reconnecting"|"offline") => void, for realtimeStore
// Returns { reconnect(), close(), mode() }.
export function createSseStream({ url, request, onOpen, onMessage, onMode }) {
  let controller = null;
  let attempts = 0;
  let mode = "connecting";
  let reconnectTimer = null;
  let closed = false;

  const setMode = (m) => { if (m !== mode) { mode = m; try { onMode && onMode(m); } catch {} } };

  function clearTimer() { if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; } }

  function scheduleReconnect() {
    if (closed) return;
    clearTimer();
    setMode(attempts > 0 ? "reconnecting" : "connecting");
    const wait = backoff(attempts++);
    reconnectTimer = setTimeout(connect, wait);
  }

  async function connect() {
    if (closed || !url) { setMode("reconnecting"); return; }
    clearTimer();

    controller = new AbortController();
    let res;
    try {
      res = await request(url, { headers: { Accept: "text/event-stream" }, signal: controller.signal });
    } catch (e) {
      if (e && e.name === "AbortError") return;
      scheduleReconnect();
      return;
    }
    if (closed) return;

    if (!res.ok) {
      scheduleReconnect();
      return;
    }

    // Stream is open — mark live.
    attempts = 0;
    setMode("live");
    try { onOpen && onOpen(); } catch {}

    // Pump SSE frames until the stream ends.
    const openedAt = Date.now();
    let endedCleanly = true;
    try {
      await readSseStream(res, (evt) => {
        if (evt && evt.topic && evt.type) {
          try { onMessage && onMessage(evt); } catch {}
        }
      }, controller.signal);
    } catch (e) {
      if (e && e.name === "AbortError") return; // we closed it
      endedCleanly = false;
    }
    if (closed) return;

    // A node ends a stream when the token it was opened with expires, which is routine: the dial goes
    // through the session again, so it carries the renewed token, and it happens at once. A stream
    // that ended cleanly after living no time at all is a node refusing it, and backs off like any
    // other failure. A network drop backs off too.
    if (endedCleanly && Date.now() - openedAt >= STABLE_MS) {
      attempts = 0;
      connect();
      return;
    }
    scheduleReconnect();
  }

  // User-driven "reconnect now": drop the backoff and re-open immediately.
  function reconnect() {
    if (closed) return;
    clearTimer();
    attempts = 0;
    if (controller) { try { controller.abort(); } catch {} controller = null; }
    connect();
  }
  function close() {
    closed = true;
    clearTimer();
    if (controller) { try { controller.abort(); } catch {} controller = null; }
    setMode("offline");
  }

  connect();
  return { reconnect, close, mode: () => mode };
}
